import { type Dirent, existsSync, readdirSync, type Stats, statSync } from "node:fs";
import { homedir } from "node:os";
import { join, relative, sep } from "node:path";
import type { DiscoveredFile } from "@tktuner/core";

const SESSION_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.jsonl$/i;
const AGENT_RE = /^agent-([0-9a-z_-]+)\.jsonl$/i;
/** Directories inside project dirs that never contain session logs. */
const SKIP_DIRS = new Set(["memory", "tool-results"]);

export function detectClaudeRoot(override?: string): { found: boolean; rootDirs: string[] } {
  const root = override ?? join(homedir(), ".claude", "projects");
  return existsSync(root) ? { found: true, rootDirs: [root] } : { found: false, rootDirs: [] };
}

/**
 * Recursively lists every session-log JSONL under the projects root.
 * Layout (verified against Claude Code 2.1.x):
 *   <project>/<session-uuid>.jsonl                          -> session
 *   <project>/<session-uuid>/subagents/agent-<id>.jsonl     -> subagent
 *   <project>/<session-uuid>/subagents/workflows/wf_<id>/agent-<id>.jsonl -> subagent
 *   .../workflows/wf_<id>/journal.jsonl                     -> skipped (no usage)
 * Anything else that ends in .jsonl is kept as 'other' and parsed defensively.
 */
export function listLogFiles(rootDirs: string[]): DiscoveredFile[] {
  const out: DiscoveredFile[] = [];
  for (const root of rootDirs) {
    let entries: Dirent[];
    try {
      entries = readdirSync(root, { recursive: true, withFileTypes: true });
    } catch {
      continue;
    }
    for (const ent of entries) {
      if (!ent.isFile() || !ent.name.endsWith(".jsonl")) continue;
      if (ent.name === "journal.jsonl") continue;
      const dirent = ent as unknown as { parentPath?: string; path?: string };
      const parent = dirent.parentPath ?? dirent.path ?? root;
      const full = join(parent, ent.name);
      const segs = relative(root, full).split(sep);
      if (segs.some((s) => SKIP_DIRS.has(s))) continue;

      let st: Stats;
      try {
        st = statSync(full);
      } catch {
        continue; // deleted between listing and stat
      }
      const base = { path: full, sizeBytes: st.size, mtimeMs: Math.trunc(st.mtimeMs) };

      const agentMatch = AGENT_RE.exec(ent.name);
      const subIdx = segs.indexOf("subagents");
      if (agentMatch && subIdx > 0) {
        out.push({
          ...base,
          kind: "subagent",
          agentId: agentMatch[1],
          parentSessionId: segs[subIdx - 1],
        });
      } else if (segs.length === 2 && SESSION_RE.test(ent.name)) {
        out.push({ ...base, kind: "session", sessionId: ent.name.slice(0, -".jsonl".length) });
      } else {
        out.push({ ...base, kind: "other" });
      }
    }
  }
  out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  return out;
}
